import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm} from "react-hook-form";
import {Autocomplete, FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React from "react"
import Box from "@mui/material/Box";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {SearchData} from "@/widget/SearchData/SearchData";
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";
import {FieldworkVO, useCreateFieldwork, useQueryFieldwork, useUpdateFieldwork} from "@/service/fieldwork.s";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {checkQueryKey, emptyValue, filterUsers, getSelectValues, iso2Date, setValues} from "@/helper/method";
import {CustomerPO} from "@/model/customer";
import {RoleUserVO} from "@/service/user.s";
import {TagOut} from "@/widget/TagOut/TagOut";
import {useRoleUsers} from "@/store/user.c";

const validationSchema = z.object({
    id: z.number().default(0),
    customerName: z.string(),
    customerId: z.number({message: "请选择拜访客户"}),
    startTime: z.unknown(),
    endTime: z.unknown(),
    travelTime: z.array(z.date({message: "请选择出行时间"}), z.date({message: "请选择出行时间"})).nonempty({message: '请选择出行时间'}),
    business: z.string({message: "请填写业务内容"}).nonempty({message: "请填写业务内容"}),
    followers: z.ostring().nullable(),
    address: z.ostring().nullable(),
    followerIds: z.ostring().nullable(),
    tempFollowers: z.array(z.object({username: z.string(), id: z.number()})).optional(),
});

export type WriteFieldworkForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export const WriteFieldwork = () => {
    const allUser = useRoleUsers([])
    const {setToast} = useGlobalActions()
    const {mutateAsync: createFieldworkMutate, isPending: createFieldworkPending} = useCreateFieldwork()
    const {mutateAsync: updateFieldworkMutate, isPending: updateFieldworkPending} = useUpdateFieldwork()
    const {onSearch, id: drawerId, write, execute} = useDrawerModal()
    const {data: fieldworkData} = useQueryFieldwork({id: drawerId!}, checkQueryKey(drawerId))
    const fieldwork = fieldworkData?.data as FieldworkVO
    const {setDrawerModal} = useGlobalActions()

    const initValues = () => {
        if (write == 'edit') {
            return {
                ...fieldwork, travelTime: [iso2Date(fieldwork?.startTime), iso2Date(fieldwork?.endTime)],
                tempFollowers: filterUsers(allUser, fieldwork?.followerIds)
            }
        }
        return {}
    }
    const {
        register,
        control,
        setValue,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm<WriteFieldworkForm>({
        values: initValues() as WriteFieldworkForm,
        resolver: zodResolver(validationSchema),
        defaultValues,
    });

    const onSubmit = async (values: WriteFieldworkForm) => {
        const result = write == 'add' ? await createFieldworkMutate(values) : await updateFieldworkMutate({...values, ...execute, id: drawerId!})
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
                <Grid size={6}>
                    <SearchData<WriteFieldworkForm, CustomerPO> control={control} freeSolo={true} attr={"customerName"} attrId={"customerId"} errMsg={errors.customerId?.message}
                                                                setValue={setValue}
                                                                label={"拜访客户"}/>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>出行时间</FormLabel>
                        <DatePickers>
                            <Controller control={control} render={({field: {onChange, value}}) => {
                                const [startDate, endDate] = value || []
                                return <DatePicker
                                    showYearDropdown={true}
                                    selectsRange={true}
                                    showMonthDropdown={true}
                                    startDate={startDate}
                                    endDate={endDate}
                                    dateFormat="yyyy/MM/dd"
                                    onChange={(date: [Date | null, Date | null]) => {
                                        const [startTime, endTime] = date
                                        setValue("startTime", startTime)
                                        setValue("endTime", endTime)
                                        onChange(date)
                                    }}
                                    locale='zhCN'
                                    placeholderText={"选择出行时间"}
                                    customInput={<TextField error={!!errors?.travelTime} helperText={errors?.travelTime?.message} fullWidth={true} sx={{
                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                    }}
                                    />}
                                />
                            }} name={'travelTime'}/>
                        </DatePickers>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>随行人员</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <Autocomplete
                                multiple={true}
                                options={allUser}
                                value={value}
                                disableListWrap={true}
                                onChange={(_, options) => {
                                    const values = getSelectValues(options, {key: "username", val: 'id'}, {key: "followerIds", val: "followers"})
                                    setValues(values, setValue as any)
                                    onChange(options)
                                }}
                                renderTags={(values) => {
                                    return values?.map((value: RoleUserVO, index: number) => <TagOut key={index} index={index} label={value.username}/>)
                                }}
                                isOptionEqualToValue={(option, value) => option.username === value.username}
                                disableCloseOnSelect={true}
                                getOptionLabel={(option: RoleUserVO) => option.username}
                                getOptionKey={(option: RoleUserVO) => option.username}
                                renderInput={(params) => (
                                    <TextField
                                        sx={{"& .MuiOutlinedInput-root": {height: 'auto', minHeight: '2.5rem'}}}
                                        placeholder={"选择随行人员"} {...params} size={'small'}/>
                                )}
                            />
                        }}
                                    name={"tempFollowers"}/>
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel>业务内容</FormLabel>
                        <TextField
                            multiline
                            rows={3}
                            error={!!errors?.business} helperText={errors?.business?.message}
                            {...register("business")}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto'},
                                    inputProps: {maxLength: 250},
                                },
                            }}
                            placeholder="填写业务内容"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
                <Grid size={12}>
                    <FormControl fullWidth={true}>
                        <FormLabel>拜访地址</FormLabel>
                        <TextField
                            multiline
                            rows={3}
                            {...register("address")}
                            slotProps={{
                                input: {
                                    sx: {height: 'auto'},
                                    inputProps: {maxLength: 250},
                                },
                            }}
                            placeholder="填写业务内容"
                            fullWidth
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createFieldworkPending || updateFieldworkPending} onReset={() => reset(defaultValues)}/>
        </Box>
    );
};
